import React, { useState } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import {
  useUpdateMemberCountryStatus,
  useSearchMemberCountries,
  useFilterMemberCountries
} from '@/lib/api/hooks/memberCountry';
import type { MemberCountry } from '@/lib/api/types';
import { showToast } from '@/lib/toast';
import { Loader } from '@/components/ui/Loader';
import { Button, Pagination, PublishModal, UnpublishModal } from '@/components/ui';
import MemberCountryRegionStats from '@/pages/about/member-countries/MemberCountryRegionStats';
import { Eye, Pencil, Globe, GlobeLock } from 'lucide-react';
import { Paragraph } from '@/components/ui/Typography';
import Search from '@/components/ui/Search';
import { useSearchFilterParams } from '@/hooks/useSearchFilterParams';

type TabType = 'PUBLISH' | 'DRAFT';

const MemberCountryRegion: React.FC = () => {
  const { regionId } = useParams<{ regionId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const regionName = location.state?.regionName || 'Region';
  const regionTag = location.state?.regionTag;

  const [activeTab, setActiveTab] = useState<TabType>('PUBLISH');
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [unpublishModalOpen, setUnpublishModalOpen] = useState(false);
  const [selectedCountryForStatusUpdate, setSelectedCountryForStatusUpdate] = useState<MemberCountry | null>(null);


        // ── URL-driven search / filter state ──────────────────────────────────────
        const {
          mode,
          searchTerm,
          // filterState,
          page,
          size,
          setSearch,
          // setFilters,
          // removeFilter,
          // clearFilters,
          setPage,
          apiFilters,
          // getActiveFilterCount
        } = useSearchFilterParams([])
      
    
    
      // Search API (when searching)
      const { data: searchData, isLoading: isSearchLoading, isError: isSearchError, refetch: refetchSearch } = useSearchMemberCountries(
        { page, size },
      searchTerm,
        mode === 'search' 
      )
    
      // Filter API (when filtering or default)
      const { data: filterData, isLoading: isFilterLoading, isError: isFilterError, refetch: refetchFilter } = useFilterMemberCountries(
        { page, size },
        {regionId: regionId, ...apiFilters },
        mode === 'filter' || mode === 'default'
      )
    
      // Select appropriate data source: search takes priority
      const aggregateData = mode === 'search' ? searchData : filterData
      const isLoading = mode === 'search' ? isSearchLoading : isFilterLoading
      const isError = mode === 'search' ? isSearchError : isFilterError
    
      const totalPages = aggregateData?.data?.totalPage ?? 1
      // const activeFilterCount = getActiveFilterCount()
      const refetchData = mode === 'search' ? refetchSearch : refetchFilter
      const emptyMessage = mode === 'search'
        ? 'No countries found matching your search criteria.'
        : mode === "filter" ?  'No countries found matching your filter criteria.'
        : 'No countries found.'

        const filteredCountries = Array.isArray(aggregateData?.data?.data)
          ? aggregateData.data.data
          : [];

  // Reset page when search or tab changes
  // useEffect(() => {
  //   setPage(1);
  // }, [debouncedSearch, activeTab]);

  // Use search API when search query is present
  // const { data: searchData, isLoading: isSearchLoading } = useSearchMemberCountries(
  //   { page, size: 20 },
  //   debouncedSearch,
  //   hasSearchQuery
  // );

  // Use filter API when no search query
  // const { data: allMemberCountriesData, isLoading: isFilterLoading, refetch } = useAllMemberCountries();
  // const { data: memberCountriesData, isLoading: isFilterLoading, refetch } = useAllMemberCountries({
  //   regionId,
  //   status: activeTab
  // });

  // const memberCountriesData = useMemo(() => {
  //   if (!allMemberCountriesData?.data) return [];
  //   return allMemberCountriesData.data.filter((country: { regionId: string; status: string; countryName: string; }) => {
  //     return (
  //       country.regionId === regionId &&
  //       country.status === activeTab &&
  //       (!debouncedSearch || country.countryName.toLowerCase().includes(debouncedSearch.toLowerCase()))
  //     );
  //   });
  // }, [allMemberCountriesData, regionId, activeTab, debouncedSearch]);
    

  const updateStatusMutation = useUpdateMemberCountryStatus();

  // Extract countries from paginated response
  // const rawCountries = hasSearchQuery
  //   ? (Array.isArray(searchData?.data?.data) ? searchData.data.data : [])
  //   : (Array.isArray(memberCountriesData) ? memberCountriesData : []);

  // Filter search results by status and region to match active tab and region
  // const filteredCountries = hasSearchQuery
  //   ? rawCountries.filter((country: MemberCountry) =>
  //     country.status === activeTab &&
  //     (!regionId || country.regionId === regionId)
  //   )
  //   : rawCountries;

  // const isLoading = hasSearchQuery ? isSearchLoading : isFilterLoading;

  const handleAddCountry = () => {
    navigate(`/admin/member-countries/${regionId}/add`, {
      state: { regionName, regionId, regionTag }
    });
  };

  const handleViewCountry = (country: MemberCountry) => {
    navigate(`/admin/member-countries/${regionId}/${country.memberCountryId}`, {
      state: { regionName, regionId, regionTag }
    });
  };

  const handleEditCountry = (country: MemberCountry) => {
    navigate(`/admin/member-countries/${regionId}/edit/${country.memberCountryId}`, {
      state: { regionName, regionId, regionTag, country }
    });
  };

  const handlePublishCountry = (country: MemberCountry) => {
    setSelectedCountryForStatusUpdate(country);
    setPublishModalOpen(true);
  };

  const handleUnpublishCountry = (country: MemberCountry) => {
    setSelectedCountryForStatusUpdate(country);
    setUnpublishModalOpen(true);
  };

  const confirmStatusUpdate = () => {
    if (!selectedCountryForStatusUpdate) return;

    const newStatus = selectedCountryForStatusUpdate.status === 'PUBLISH' ? 'DRAFT' : 'PUBLISH';

    updateStatusMutation.mutate(
      {
        memberCountryId: selectedCountryForStatusUpdate.memberCountryId,
        data: { status: newStatus }
      },
      {
        onSuccess: () => {
          showToast.success(
            newStatus === 'PUBLISH'
              ? 'Member country published successfully!'
              : 'Member country unpublished successfully!'
          );
          setPublishModalOpen(false);
          setUnpublishModalOpen(false);
          setSelectedCountryForStatusUpdate(null);
          refetchFilter();
        },
        onError: (error: Error) => {
          showToast.error('Failed to update status: ' + (error.message || 'Unknown error'));
        }
      }
    );
  };

  const isEmptyState = !isLoading && mode === "default" && filteredCountries.length === 0;

  return (
    <div className="space-y-4 flex flex-col gap-6">

      <div className="">
        <div className="flex items-center gap-2 text-sm mb-4">
          <Link to="/admin/member-countries" className="text-[#047857] hover:underline">
            Members Country
          </Link>
          <span className="text-gray-400">▸</span>
          <span className="text-gray-600">{regionName}</span>
        </div>
        <div className='flex flex-col gap-1'>
        <h1 className="text-2xl font-bold text-gray-900">{regionName}</h1>
    <p>Manage countries within this region</p>
        </div>
      </div>

      {/* Country Region Stats */}
      <MemberCountryRegionStats regionId={regionId} />

      {/* Tabs */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-0">
          <button
            onClick={() => {
              setActiveTab('PUBLISH');
              setPage(1);
            }}
            className={`px-6 py-2.5 text-sm font-semibold transition-colors rounded-md ${activeTab === 'PUBLISH'
                ? 'bg-wine-100 text-white'
                : 'text-gray-700 hover:bg-gray-100'
              }`}
          >
            Published
          </button>
          <button
            onClick={() => {
              setActiveTab('DRAFT');
              setPage(1);
            }}
            className={`px-6 py-2.5 text-sm font-semibold transition-colors rounded-md ${activeTab === 'DRAFT'
                ? 'bg-wine-100 text-white'
                : 'text-gray-700 hover:bg-gray-100'
              }`}
          >
            Drafts
          </button>
        </div>
        <button
        style={{display: "none"}}
          onClick={handleAddCountry}
          className="bg-[#047857] hover:bg-[#059669] text-white font-semibold py-2.5 px-6 rounded-md text-sm"
        >
          Add Country
        </button>
      </div>

      {/* Search */}
      <div className="flex items-center justify-between gap-4">
        {/* <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-0 focus:outline-none bg-[#FAFAFA]"
          />
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <SearchIcon className="w-5 h-5 text-gray-400" />
          </div>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
              type="button"
            >
              <CloseIcon className="w-4 h-4" />
            </button>
          )}
        </div> */}
           <Search
                        value={searchTerm}
                        onSearch={setSearch}
                        placeholder="Search countries..."
                      />
      </div>

      {/* Countries List */}
      <div>
        {isLoading ? (
          <Loader size="lg" variant="wine" message="Loading countries..." />
        ) : isError ? (
          <div className="p-8 flex items-center justify-center  flex-col gap-4 text-center">
            <Paragraph className="">Error loading data.</Paragraph>
            <Button
              onClick={() => {
                refetchData();
              }}
              className=""
            >
              Retry
            </Button>
          </div>
        ) :  isEmptyState ? <div className="text-center py-20">
                        <div className="inline-block bg-gray-100 p-4 rounded-full mb-4">
                          <img src="/No-post-icon.png" alt="No country" className="w-20 h-20 object-contain" />
                        </div>
                        <h3 className="font-semibold text-gray-800 text-lg">
                          {emptyMessage}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                          { activeTab === 'PUBLISH' ? 'Upload your first country to get started' :  activeTab === 'DRAFT' ? 'No draft countries yet' : 'No Data Found'}
                        </p>
                          {/* <Button variant="primary" className="mt-4" onClick={handleAddCountry}>
                            <PlusIcon className="w-4 h-4" />
                            Add Country
                          </Button> */}
                      </div> : filteredCountries.length === 0 ? (
          <div className="text-center py-20">
            <div className="inline-block bg-gray-100 p-4 rounded-full mb-4">
              <img src="/No-post-icon.png" alt="No country" className="w-20 h-20 object-contain" />
            </div>
            <h3 className="font-semibold text-gray-800 text-lg">
              {emptyMessage}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              { activeTab === 'PUBLISH' ? 'Upload your first country to get started' :  activeTab === 'DRAFT' ? 'No draft countries yet' : 'No Data Found'}
            </p>
          </div>
        ) : (
          <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-6">
            {filteredCountries.map((country: MemberCountry) => (
              // Member Country Card
              <div
                key={country.memberCountryId}
                onClick={() => handleViewCountry(country)}
                className="group relative cursor-pointer overflow-hidden rounded-xl border border-gray-200 bg-white transition-all hover:border-wine-100 hover:shadow-md"
              >
                {/* Flag Image */}
                <div className="relative h-28 lg:h-40 bg-gray-100 overflow-hidden rounded-t-xl">
                  {country.flagUrl && country.flagUrl !== 'N/A' ? (
                    <img
                      src={country.flagUrl}
                      alt={country.countryName || country.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-200">
                      <span className="text-2xl font-bold text-gray-400">
                        {(country.countryName || country.title || '?').charAt(0)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Country Info */}
                <div className="relative p-4">
                  <h3 className="pr-8 font-semibold text-gray-900 text-[1.03rem] leading-7 line-clamp-2 min-h-[3.5rem]">
                    {country.countryName || country.title}
                  </h3>

                  <div className="mt-2">
                    <span
                      className={`inline-flex rounded-md px-2 py-1 text-sm font-semibold bg-[#E8F6EE] text-[#0F8A47]`}
                    >
                      Published
                    </span>
                    {/* <span
                      className={`inline-flex rounded-md px-2 py-1 text-sm font-semibold ${
                        country.status === 'PUBLISH' ? 'bg-[#E8F6EE] text-[#0F8A47]' : 'bg-[#FDF3E6] text-[#B86A00]'
                      }`}
                    >
                      {country.status === 'PUBLISH' ? 'Published' : 'Draft'}
                    </span> */}
                  </div>

                  <div className="mt-3 flex items-center justify-between text-sm text-gray-500">
                    <span>Last updated</span>
                    <span className="font-medium text-gray-500">
                      {new Date(country.updatedAt || country.dateJoined || '').toLocaleDateString('en-US', {
                        month: 'short',
                        day: '2-digit',
                        year: 'numeric'
                      })}
                    </span>
                  </div>

                </div>

                <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 border-t border-gray-200 bg-[#FAFAFA] opacity-0 translate-y-full transition-all duration-200 group-hover:pointer-events-auto group-hover:opacity-100 group-hover:translate-y-0">
                  <div className="grid grid-cols-3">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewCountry(country);
                      }}
                      className="flex h-12 items-center justify-center border-r border-gray-200 text-gray-600 hover:bg-gray-100"
                      title="View"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditCountry(country);
                      }}
                      className="flex h-12 items-center justify-center border-r border-gray-200 text-gray-600 hover:bg-gray-100"
                      title="Edit"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    {/* Shortcut publish/unpublish */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (country.status === 'PUBLISH') {
                          handleUnpublishCountry(country);
                        } else {
                          handlePublishCountry(country);
                        }
                      }}
                      className="flex h-12 items-center justify-center text-gray-600 hover:bg-gray-100"
                      title={country.status === 'PUBLISH' ? 'Unpublish' : 'Publish'}
                    >
                      {country.status === 'PUBLISH' ? (
                        <GlobeLock className="h-4 w-4" />
                      ) : (
                        <Globe className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
               <Pagination
                                      currentPage={page}
                                      totalPages={totalPages}
                                      onPageChange={setPage}
                                      className="p-4"
                                    />
          </>
        )}
      </div>

      {/* Publish Modal */}
      {selectedCountryForStatusUpdate && selectedCountryForStatusUpdate.status === 'DRAFT' && (
        <PublishModal
          isOpen={publishModalOpen}
          onClose={() => setPublishModalOpen(false)}
          onConfirm={confirmStatusUpdate}
          title="Publish Country?"
          subtitle="Are you sure you want to publish this country?"
          itemName={selectedCountryForStatusUpdate.title}
          infoText="This country will be visible to the public once published."
          itemType="Country"
          isLoading={updateStatusMutation.isPending}
        />
      )}

      {/* Unpublish Modal */}
      {selectedCountryForStatusUpdate && selectedCountryForStatusUpdate.status === 'PUBLISH' && (
        <UnpublishModal
          isOpen={unpublishModalOpen}
          onClose={() => setUnpublishModalOpen(false)}
          onConfirm={confirmStatusUpdate}
          itemType="Country"
          isLoading={updateStatusMutation.isPending}
        />
      )}
    </div>
  );
};

export default MemberCountryRegion;
